import {
    $createRangeSelection,
    $getNodeByKey,
    $getSelection,
    $isRangeSelection,
    $isTextNode,
    $setSelection,
    type LexicalNode,
    type RangeSelection,
} from "lexical";

export type TextRangeRef = {
    anchorKey: string;
    anchorOffset: number;
    focusKey: string;
    focusOffset: number;
};

export function $isSameTextRange(
    a: TextRangeRef | null,
    b: TextRangeRef | null,
): boolean {
    if (a === null || b === null) {
        return a === b;
    }
    return (
        a.anchorKey === b.anchorKey &&
    a.anchorOffset === b.anchorOffset &&
    a.focusKey === b.focusKey &&
    a.focusOffset === b.focusOffset
    );
}

/** Range from Lexical selection (anchor/focus as stored; use for replace after normalize). */
export function $textRangeFromRangeSelection(
    sel: RangeSelection,
): TextRangeRef | null {
    const anchor = sel.anchor;
    const focus = sel.focus;
    if (anchor.type !== "text" || focus.type !== "text") {
        return null;
    }
    return {
        anchorKey: anchor.key,
        anchorOffset: anchor.offset,
        focusKey: focus.key,
        focusOffset: focus.offset,
    };
}

/** Document-ordered range for replacement (start before end in document order). */
export function $orderedTextRange(sel: RangeSelection): TextRangeRef | null {
    const pts = sel.getStartEndPoints();
    if (!pts) {
        return null;
    }
    const [start, end] = pts;
    if (start.type !== "text" || end.type !== "text") {
        return null;
    }
    return {
        anchorKey: start.key,
        anchorOffset: start.offset,
        focusKey: end.key,
        focusOffset: end.offset,
    };
}

export function $applyTextRangeReplace(
    range: TextRangeRef,
    nodes: LexicalNode[],
    trailingSpace = true,
): void {
    const rangeSel = $createRangeSelection();
    rangeSel.anchor.set(range.anchorKey, range.anchorOffset, "text");
    rangeSel.focus.set(range.focusKey, range.focusOffset, "text");
    $setSelection(rangeSel);
    const sel = $getSelection();
    if (!$isRangeSelection(sel)) {
        return;
    }
    sel.removeText();
    if (nodes.length > 0) {
        sel.insertNodes(nodes);
    }
    if (trailingSpace) {
        const next = $getSelection();
        if ($isRangeSelection(next) && next.isCollapsed()) {
            const n = next.anchor.getNode();
            if ($isTextNode(n)) {
                const after = n.getTextContent().slice(next.anchor.offset);
                if (after.length === 0 || !/^\s/.test(after)) {
                    n.spliceText(next.anchor.offset, 0, " ");
                }
            }
        }
    }
}

export function $getCollapsedSlashQuery(
    sel: RangeSelection,
): {range: TextRangeRef; query: string} | null {
    if (!sel.isCollapsed() || sel.anchor.type !== "text") {
        return null;
    }
    const textNode = sel.anchor.getNode();
    if (!$isTextNode(textNode)) {
        return null;
    }
    const offset = sel.anchor.offset;
    const text = textNode.getTextContent();
    const before = text.slice(0, offset);
    const slashIdx = before.lastIndexOf("/");
    if (slashIdx < 0) {
        return null;
    }
    const fromSlash = before.slice(slashIdx);
    if (!/^\/[\w-]*$/.test(fromSlash)) {
        return null;
    }
    const charBefore = slashIdx > 0 ? before[slashIdx - 1] : "";
    if (charBefore && /[A-Za-z0-9_]/.test(charBefore)) {
        return null;
    }
    return {
        range: {
            anchorKey: textNode.getKey(),
            anchorOffset: slashIdx,
            focusKey: textNode.getKey(),
            focusOffset: offset,
        },
        query: fromSlash.slice(1),
    };
}

function fixFocusOffset(
    textNode: ReturnType<typeof $getNodeByKey>,
    focusOffset: number,
): number {
    if (!$isTextNode(textNode)) {
        return focusOffset;
    }
    return Math.min(focusOffset, textNode.getTextContent().length);
}

export function $getSelectedSlashCommand(
    sel: RangeSelection,
): {range: TextRangeRef; word: string} | null {
    if (sel.isCollapsed()) {
        return null;
    }
    const ordered = $orderedTextRange(sel);
    if (!ordered) {
        return null;
    }
    const anchorNode = $getNodeByKey(ordered.anchorKey);
    const focusNode = $getNodeByKey(ordered.focusKey);
    if (!$isTextNode(anchorNode) || focusNode !== anchorNode) {
        return null;
    }
    const endOff = fixFocusOffset(focusNode, ordered.focusOffset);
    const startOff = ordered.anchorOffset;
    const slice = anchorNode.getTextContent().slice(startOff, endOff);
    const m = /^\/([\w-]+)$/.exec(slice);
    if (!m) {
        return null;
    }
    return {
        range: {
            anchorKey: ordered.anchorKey,
            anchorOffset: startOff,
            focusKey: ordered.focusKey,
            focusOffset: startOff + m[0].length,
        },
        word: m[1],
    };
}

/** Same boundary rules as {@link $getCollapsedSlashQuery} — `/` not preceded by word chars.
 * Avoid lookbehind (`(?<!…)`) — unsupported in Safari before 16.4. */
export const SLASH_ENTITY_IN_TEXT_RE = /\/[\w-]+/g;

export const SLASH_ENTITY_TEXT_COLOR = "#4088F8";

export type SlashEntityTextSegment = {
    text: string;
    isSlashEntity: boolean;
};

function isSlashEntityAt(text: string, index: number): boolean {
    const charBefore = index > 0 ? text[index - 1] : "";
    return !(charBefore && /[A-Za-z0-9_]/.test(charBefore));
}

export function splitTextWithSlashEntities(text: string): SlashEntityTextSegment[] {
    const segments: SlashEntityTextSegment[] = [];
    let lastIndex = 0;
    let m: RegExpExecArray | null;
    const re = new RegExp(SLASH_ENTITY_IN_TEXT_RE.source, "g");
    while ((m = re.exec(text)) !== null) {
        if (!isSlashEntityAt(text, m.index)) {
            continue;
        }
        if (m.index > lastIndex) {
            segments.push({
                text: text.slice(lastIndex, m.index),
                isSlashEntity: false,
            });
        }
        segments.push({text: m[0], isSlashEntity: true});
        lastIndex = m.index + m[0].length;
    }
    if (lastIndex < text.length) {
        segments.push({text: text.slice(lastIndex), isSlashEntity: false});
    }
    if (segments.length === 0 && text.length > 0) {
        segments.push({text, isSlashEntity: false});
    }
    return segments;
}
